<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import {
  mapFilterToFileType,
  CONTACT_MEDIA_FILTERS,
} from 'dashboard/helper/contactMediaHelper';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  contactId: { type: [String, Number], default: null },
});
const route = useRoute();
const { t } = useI18n();
const VISUAL_TYPES = ['image', 'video', 'ig_reel'];
const VIDEO_TYPES = ['video', 'ig_reel'];
const isVideoItem = item => VIDEO_TYPES.includes(item.file_type);

const items = ref([]);
const page = ref(1);
const totalCount = ref(0);
const activeFilter = ref('all');
const isLoading = ref(false);
const galleryAttachment = ref(null);
const showGallery = ref(false);

const visualItems = computed(() =>
  items.value.filter(i => VISUAL_TYPES.includes(i.file_type))
);
const fileItems = computed(() =>
  items.value.filter(i => !VISUAL_TYPES.includes(i.file_type))
);
const hasMore = computed(() => items.value.length < totalCount.value);
const isEmpty = computed(() => !isLoading.value && items.value.length === 0);

const fetchMedia = async ({ reset = false } = {}) => {
  const contactId = props.contactId || route.params.contactId;
  if (!contactId) return;
  if (reset) {
    page.value = 1;
    items.value = [];
    totalCount.value = 0;
  }
  isLoading.value = true;
  try {
    const { data } = await ContactAPI.getAttachments(contactId, {
      page: page.value,
      fileType: mapFilterToFileType(activeFilter.value),
    });
    items.value = reset ? data.payload : [...items.value, ...data.payload];
    totalCount.value = data.meta.total_count;
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.MEDIA.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const changeFilter = filter => {
  if (filter === activeFilter.value) return;
  activeFilter.value = filter;
  fetchMedia({ reset: true });
};

const loadMore = () => {
  page.value += 1;
  fetchMedia();
};

const openGallery = attachment => {
  galleryAttachment.value = attachment;
  showGallery.value = true;
};

onMounted(() => fetchMedia({ reset: true }));
</script>

<template>
  <div class="flex flex-col gap-4 px-6 py-4">
    <div class="flex flex-wrap gap-2">
      <button
        v-for="filter in CONTACT_MEDIA_FILTERS"
        :key="filter"
        :data-testid="`filter-${filter}`"
        class="px-3 py-1 text-sm rounded-lg"
        :class="
          activeFilter === filter
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-black2 text-n-slate-11'
        "
        @click="changeFilter(filter)"
      >
        {{ t(`CONTACTS_LAYOUT.SIDEBAR.MEDIA.FILTERS.${filter.toUpperCase()}`) }}
      </button>
    </div>

    <div
      v-if="isLoading && !items.length"
      class="flex justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <div
      v-else-if="isEmpty"
      data-testid="media-empty"
      class="py-10 text-sm text-center text-n-slate-11"
    >
      {{ t('CONTACTS_LAYOUT.SIDEBAR.MEDIA.EMPTY') }}
    </div>

    <template v-else>
      <div v-if="visualItems.length" class="grid grid-cols-3 gap-2">
        <button
          v-for="item in visualItems"
          :key="item.message_id"
          data-testid="media-thumb"
          class="relative overflow-hidden rounded-lg aspect-square bg-n-alpha-black2"
          :aria-label="t('CONTACTS_LAYOUT.SIDEBAR.MEDIA.OPEN')"
          @click="openGallery(item)"
        >
          <img
            v-if="item.thumb_url || !isVideoItem(item)"
            :src="item.thumb_url || item.data_url"
            alt=""
            class="object-cover w-full h-full"
          />
          <span
            v-else
            class="flex h-full w-full items-center justify-center bg-n-slate-3"
          >
            <span class="i-lucide-film size-6 text-n-slate-10" />
          </span>
          <span
            v-if="isVideoItem(item)"
            class="absolute inset-0 flex items-center justify-center"
          >
            <span
              class="i-lucide-play flex size-8 items-center justify-center rounded-full bg-black/50 p-1.5 text-white"
            />
          </span>
        </button>
      </div>

      <ul v-if="fileItems.length" class="flex flex-col gap-2">
        <li
          v-for="item in fileItems"
          :key="item.message_id"
          data-testid="media-file"
          class="flex items-center justify-between px-3 py-2 rounded-lg bg-n-alpha-black2"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ item.extension || item.file_type }}
          </span>
          <a
            :href="item.data_url"
            target="_blank"
            rel="noopener noreferrer"
            class="text-sm text-n-brand"
          >
            {{ t('CONTACTS_LAYOUT.SIDEBAR.MEDIA.DOWNLOAD') }}
          </a>
        </li>
      </ul>

      <button
        v-if="hasMore"
        data-testid="load-more"
        :disabled="isLoading"
        class="self-center px-4 py-2 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-11 disabled:opacity-50 disabled:cursor-not-allowed"
        @click="loadMore"
      >
        {{ t('CONTACTS_LAYOUT.SIDEBAR.MEDIA.LOAD_MORE') }}
      </button>
    </template>

    <GalleryView
      v-if="showGallery"
      v-model:show="showGallery"
      :attachment="galleryAttachment"
      :all-attachments="visualItems"
      @close="() => (showGallery = false)"
    />
  </div>
</template>
